import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Row, Col, Button, Table, Modal, Form, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import Swal from "sweetalert2";

const API = process.env.REACT_APP_API;

const emptyForm = { period_order: "", period_begin: "", period_end: "" };

// การ์ดคาบเรียน 1 รายการ สำหรับมุมมองมือถือ
const PeriodCard = ({ p, onEdit, onDelete }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between">
      <div className="fw-semibold">คาบที่ {p.period_order}</div>
      <div>
        <Button size="sm" variant="outline-secondary" className="me-1" onClick={onEdit}>
          <i className="ri-edit-line"></i>
        </Button>
        <Button size="sm" variant="outline-danger" onClick={onDelete}>
          <i className="ri-delete-bin-line"></i>
        </Button>
      </div>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">เวลาเริ่ม:</span> {p.period_begin}</div>
      <div><span className="text-muted">เวลาสิ้นสุด:</span> {p.period_end}</div>
    </div>
  </div>
)

export default function ManagePeriod() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (s === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { setLoading } = useLoading();

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearLabel, setYearLabel] = useState("");
  const [periods, setPeriods] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const authHeader = useCallback(() => ({
    headers: { authorization: `Bearer ${getToken()}` },
  }), []);

  // ─── Load year list ───────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, authHeader()).then((res) => {
      const list = res.data;
      setYearList(list || []);
      let id = localStorage.getItem("yearId");
      if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
      setYearId(id);
      const sel = list.find((y) => String(y.year_id) === String(id));
      setYearLabel(sel ? `ปีการศึกษา ${sel.year_year}` : "");
    }).catch(() => {});
  }, []);

  const handleSelectYear = (id) => {
    const sel = yearList.find((y) => y.year_id === id);
    if (!sel) return;
    setYearId(id);
    setYearLabel(`ปีการศึกษา ${sel.year_year}`);
    localStorage.setItem("yearId", id);
  };

  // ─── Load periods ─────────────────────────────────────────────────────────
  const loadPeriods = useCallback(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`${API}/getPeriodList/${yearId}`, authHeader())
      .then((res) => setPeriods(res.data))
      .catch(() => setPeriods([]))
      .finally(() => setLoading(false));
  }, [yearId, authHeader, setLoading]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  // ─── Modal helpers ────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingId(p.period_id);
    setForm({ period_order: p.period_order, period_begin: p.period_begin, period_end: p.period_end });
    setShowModal(true);
  };

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    const { period_order, period_begin, period_end } = form;
    if (!period_order || !period_begin || !period_end) {
      Swal.fire({ title: "กรุณากรอกข้อมูลให้ครบ", icon: "warning" });
      return;
    }
    try {
      if (editingId) {
        await axiosInstance.put(`${API}/updatePeriod`, {
          period_id: editingId, period_order, period_begin, period_end,
        }, authHeader());
      } else {
        await axiosInstance.post(`${API}/addPeriod`, {
          year_id: yearId, period_order, period_begin, period_end,
        }, authHeader());
      }
      setShowModal(false);
      loadPeriods();
    } catch (err) {
      Swal.fire({ title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message, icon: "error" });
    }
  };

  const handleDelete = (p) => {
    Swal.fire({
      title: `ลบคาบ ${p.period_order}?`,
      text: "การลบจะกระทบตารางสอนที่ใช้คาบนี้อยู่",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await axiosInstance.delete(`${API}/deletePeriod/${p.period_id}`, authHeader());
        loadPeriods();
      } catch (err) {
        Swal.fire({ title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message, icon: "error" });
      }
    });
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Top bar */}
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">จัดการการสอน</Link></li>
              <li className="breadcrumb-item active">จัดการคาบเรียน</li>
            </ol>
            <h4 className="main-title mb-0">จัดการคาบเรียน</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">
                {yearLabel || "เลือกปีการศึกษา"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map((y) => (
                  <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                    {`ปีการศึกษา ${y.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Row className="g-3">
          <Col xs={12}>
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <span className="fw-semibold">คาบเรียน — {yearLabel}</span>
                <div className="d-flex align-items-center gap-2">
                  <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                    <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                      onClick={() => setView('table')} title="มุมมองตาราง">
                      <i className="ri-table-line" />
                    </button>
                    <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                      onClick={() => setView('card')} title="มุมมองการ์ด">
                      <i className="ri-layout-grid-line" />
                    </button>
                  </div>
                  <Button size="sm" variant="primary" onClick={openAdd}>
                    <i className="ri-add-line me-1"></i>เพิ่มคาบเรียน
                  </Button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {periods.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="ri-time-line fs-2 d-block mb-2"></i>
                    ยังไม่มีคาบเรียนสำหรับปีการศึกษานี้
                  </div>
                ) : view === 'card' ? (
                  <div className="p-3">
                    <Row className="g-3">
                      {periods.map((p) => (
                        <Col key={p.period_id} xs={12} md={6} xl={4}>
                          <PeriodCard p={p} onEdit={() => openEdit(p)} onDelete={() => handleDelete(p)} />
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                  <Table responsive hover className="mb-0">
                    <thead className="table-secondary">
                      <tr>
                        <th className="text-center" style={{ width: 80 }}>คาบที่</th>
                        <th className="text-center">เวลาเริ่ม</th>
                        <th className="text-center">เวลาสิ้นสุด</th>
                        <th style={{ width: 120 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {periods.map((p) => (
                        <tr key={p.period_id}>
                          <td className="text-center fw-semibold">{p.period_order}</td>
                          <td className="text-center">{p.period_begin}</td>
                          <td className="text-center">{p.period_end}</td>
                          <td className="text-center">
                            <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openEdit(p)}>
                              <i className="ri-edit-line"></i>
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(p)}>
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Add / Edit Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editingId ? "แก้ไขคาบเรียน" : "เพิ่มคาบเรียน"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>คาบที่</Form.Label>
                <Form.Control
                  type="number" min="1" name="period_order"
                  value={form.period_order} onChange={handleChange}
                  placeholder="เช่น 1, 2, 3 ..."
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>เวลาเริ่ม</Form.Label>
                <Form.Control
                  type="time" name="period_begin"
                  value={form.period_begin} onChange={handleChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>เวลาสิ้นสุด</Form.Label>
                <Form.Control
                  type="time" name="period_end"
                  value={form.period_end} onChange={handleChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" onClick={handleSave}>บันทึก</Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
